import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import { ArrowRight, BookOpenCheck, Brain, ClipboardList, GraduationCap, Globe2, Lightbulb, MessageCircleQuestion, ShieldCheck, Sparkles, Target } from "lucide-react";
import "./styles.css";

type Locale = "zh" | "en";

const copy = {
  zh: {
    brand: "启序学伴",
    lang: "EN",
    nav: ["能力", "辅导", "进度"],
    label: "独立案例站点 07 / 在线教育 + AI 辅导",
    title: "一个不只给答案、而是会引导学生思考的 AI 辅导网站。",
    subtitle: "面向在线教育、课后辅导和学习平台，用启发式提示、分步讲解、错因分析和练习题帮助学生真正理解。",
    primary: "开始辅导",
    secondary: "查看能力",
    featuresTitle: "AI 辅导能力",
    featuresSubtitle: "把一次问答变成可追踪的学习过程。",
    features: [
      ["启发式提示", "先问关键问题，让学生自己迈出第一步。"],
      ["分步讲解", "把概念、公式、代入和检查拆开讲。"],
      ["错因分析", "识别常见误区，形成个性化补弱建议。"],
      ["练习生成", "根据当前问题生成同类巩固题。"]
    ],
    deskTitle: "AI 学习辅导台",
    deskSubtitle: "后端直连 MiniMax，生成适合当前年级和学科的辅导路径。",
    question: "一辆车 3 小时行驶 180 千米，照这样的速度，5 小时能行驶多少千米？请教我怎么想，而不是只给答案。",
    subject: "学科",
    level: "年级",
    mode: "模式",
    ask: "开始辅导",
    sample: "填入示例",
    waiting: "等待学生问题。",
    progressTitle: "学习进度与安全边界",
    progressSubtitle: "教育场景需要保护学生体验，避免代写作业和跳过思考过程。",
    panels: [
      ["学习记录", "记录学生问题、错因标签和掌握度变化。"],
      ["家长反馈", "把学习表现转成可读反馈，而不是只看分数。"],
      ["防代写", "默认不直接输出最终答案，先给提示和步骤。"]
    ],
    footer: "启序科技 AI 案例站点 · 第 7 个真实独立网站"
  },
  en: {
    brand: "Qixu Tutor",
    lang: "中文",
    nav: ["Features", "Tutor", "Progress"],
    label: "Standalone Case Site 07 / Online Education + AI Tutor",
    title: "An AI tutor that guides thinking instead of only giving answers.",
    subtitle: "For online education and after-class tutoring: use hints, step-by-step explanations, mistake analysis and practice generation.",
    primary: "Start tutoring",
    secondary: "View features",
    featuresTitle: "AI Tutoring Capabilities",
    featuresSubtitle: "Turn each question into a trackable learning process.",
    features: [
      ["Socratic hints", "Ask key questions before revealing the path."],
      ["Step-by-step", "Separate concepts, formulas, substitution and checks."],
      ["Mistake analysis", "Identify misconceptions and weak spots."],
      ["Practice generation", "Create similar exercises for consolidation."]
    ],
    deskTitle: "AI Tutoring Desk",
    deskSubtitle: "The backend calls MiniMax directly and adapts guidance to subject and grade.",
    question: "A car travels 180 km in 3 hours. At the same speed, how far can it travel in 5 hours? Teach me how to think, not just the answer.",
    subject: "Subject",
    level: "Level",
    mode: "Mode",
    ask: "Start tutoring",
    sample: "Use sample",
    waiting: "Waiting for a student question.",
    progressTitle: "Learning Progress and Guardrails",
    progressSubtitle: "Education products need to protect learning quality and avoid homework outsourcing.",
    panels: [
      ["Learning record", "Track questions, mistake tags and mastery changes."],
      ["Parent feedback", "Convert learning behavior into readable feedback."],
      ["Anti-cheating", "Guide with hints and steps before final answers."]
    ],
    footer: "Qixu Technology AI case site · seventh real standalone website"
  }
};

function App() {
  const [locale, setLocale] = useState<Locale>("zh");
  const t = copy[locale];
  const [question, setQuestion] = useState(t.question);
  const [subject, setSubject] = useState(locale === "zh" ? "数学" : "Math");
  const [level, setLevel] = useState(locale === "zh" ? "初中" : "Middle school");
  const [mode, setMode] = useState(locale === "zh" ? "启发式辅导" : "Socratic tutoring");
  const [result, setResult] = useState(t.waiting);
  const [meta, setMeta] = useState("");
  const [loading, setLoading] = useState(false);

  function switchLocale() {
    const next = locale === "zh" ? "en" : "zh";
    setLocale(next);
    setQuestion(copy[next].question);
    setSubject(next === "zh" ? "数学" : "Math");
    setLevel(next === "zh" ? "初中" : "Middle school");
    setMode(next === "zh" ? "启发式辅导" : "Socratic tutoring");
    setResult(copy[next].waiting);
    setMeta("");
  }

  async function tutor() {
    if (!question.trim() || loading) return;
    setLoading(true);
    setResult(locale === "zh" ? "正在生成辅导路径..." : "Generating tutoring path...");
    setMeta("");
    try {
      const response = await fetch("/api/tutor", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, subject, level, mode, locale })
      });
      const data = await response.json();
      setResult(data.text || t.waiting);
      setMeta(`${data.provider || "unknown"} / ${data.model || "unknown"}`);
    } catch {
      setResult(locale === "zh" ? "网络异常，无法完成辅导。" : "Network error. Tutoring unavailable.");
      setMeta("network fallback");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main>
      <header className="topbar">
        <a className="brand" href="#top"><span>学</span><strong>{t.brand}</strong></a>
        <nav><a href="#features">{t.nav[0]}</a><a href="#desk">{t.nav[1]}</a><a href="#progress">{t.nav[2]}</a></nav>
        <button className="lang" onClick={switchLocale}><Globe2 size={16} />{t.lang}</button>
      </header>

      <section className="hero" id="top">
        <div>
          <p className="eyebrow"><Sparkles size={16} />{t.label}</p>
          <h1>{t.title}</h1>
          <p className="subtitle">{t.subtitle}</p>
          <div className="actions">
            <a className="button primary" href="#desk">{t.primary}<ArrowRight size={18} /></a>
            <a className="button secondary" href="#features">{t.secondary}</a>
          </div>
        </div>
        <div className="hero-board">
          <div className="board-head"><GraduationCap size={22} />Learning loop</div>
          <div className="pipeline">
            <div><MessageCircleQuestion size={18} /> Ask</div>
            <div><Lightbulb size={18} /> Hint</div>
            <div><BookOpenCheck size={18} /> Explain</div>
            <div><Target size={18} /> Practice</div>
          </div>
        </div>
      </section>

      <section className="features" id="features">
        <div className="section-copy"><h2>{t.featuresTitle}</h2><p>{t.featuresSubtitle}</p></div>
        <div className="feature-grid">
          {[Lightbulb, ClipboardList, Brain, Target].map((Icon, index) => (
            <article key={t.features[index][0]}><Icon size={24} /><h3>{t.features[index][0]}</h3><p>{t.features[index][1]}</p></article>
          ))}
        </div>
      </section>

      <section className="desk-section" id="desk">
        <div className="desk">
          <div className="section-title"><GraduationCap size={22} /><div><h2>{t.deskTitle}</h2><p>{t.deskSubtitle}</p></div></div>
          <textarea value={question} onChange={(event) => setQuestion(event.target.value)} />
          <div className="controls">
            <label>{t.subject}<input value={subject} onChange={(event) => setSubject(event.target.value)} /></label>
            <label>{t.level}<input value={level} onChange={(event) => setLevel(event.target.value)} /></label>
            <label>{t.mode}<input value={mode} onChange={(event) => setMode(event.target.value)} /></label>
          </div>
          <div className="composer-actions">
            <button onClick={() => setQuestion(t.question)}>{t.sample}</button>
            <button className="primary" onClick={tutor} disabled={loading || !question.trim()}>{t.ask}<Sparkles size={16} /></button>
          </div>
        </div>
        <div className="result-card">
          <h3>Tutor output</h3>
          <p>{result}</p>
          {meta && <span>{meta}</span>}
        </div>
      </section>

      <section className="progress" id="progress">
        <div><h2>{t.progressTitle}</h2><p>{t.progressSubtitle}</p></div>
        <div className="stats">
          <div><span>掌握度</span><strong>78%</strong><em>+12%</em></div>
          <div><span>错因标签</span><strong>6</strong><em>本周</em></div>
          <div><span>练习完成</span><strong>24</strong><em>题</em></div>
        </div>
        <div className="progress-grid">
          {t.panels.map(([title, content]) => <article key={title}><ShieldCheck size={20} /><h3>{title}</h3><p>{content}</p></article>)}
        </div>
      </section>

      <footer>{t.footer}</footer>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
